import { SidebarCompactPopover } from '@/components/SidebarCompactPopover';
import { Select } from '@/components/ui/Select';
import { useOrg } from '@/contexts/OrgContext';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type PeriodBarProps = {
  variant?: 'default' | 'sidebar' | 'sidebarCompact';
};

export function PeriodBar({ variant = 'default' }: PeriodBarProps) {
  const { t } = useTranslation();
  const { periods, currentPeriodId, setCurrentPeriodId } = useOrg();

  if (!periods.length) return null;

  const periodOptions = periods.map((p) => ({
    value: p.id,
    label: `${p.name}${p.status === 'closed' ? ` (${t('period.closed')})` : ''}`
  }));

  if (variant === 'sidebarCompact') {
    return (
      <SidebarCompactPopover icon={Calendar} label={t('period.label')}>
        <p className="mb-2 text-xs font-medium text-[var(--fv-text-muted)]">{t('period.label')}</p>
        <Select
          className="block w-full"
          value={currentPeriodId ?? ''}
          onValueChange={setCurrentPeriodId}
          options={periodOptions}
          aria-label={t('period.label')}
        />
      </SidebarCompactPopover>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-xs font-medium text-[var(--fv-sidebar-muted)]">{t('period.label')}</p>
        <p className="mt-0.5 text-[10px] leading-snug text-[var(--fv-sidebar-muted)]/80">
          {t('period.hint')}
        </p>
        <Select
          className="mt-2 block w-full"
          tone="sidebar"
          value={currentPeriodId ?? ''}
          onValueChange={setCurrentPeriodId}
          options={periodOptions}
          aria-label={t('period.label')}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 border-b border-[var(--fv-border)] bg-[var(--fv-surface)]/80 px-6 py-2.5 text-sm shadow-[var(--fv-shadow-sm)] backdrop-blur-sm">
      <span className="font-medium text-[var(--fv-text-muted)]">{t('period.label')}</span>
      <Select
        className="min-w-[14rem]"
        value={currentPeriodId ?? ''}
        onValueChange={setCurrentPeriodId}
        options={periodOptions}
        aria-label={t('period.label')}
      />
    </div>
  );
}
